import { useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { clearWhitelistCache } from "../lib/whitelistService";
import { calculateTieredCommission } from "../utils/commissionTiers";

const useWhitelistManager = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { user } = useAuth();

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  // Get existing affiliate names for dropdown
  const getExistingAffiliateNames = useCallback(async () => {
    try {
      // Get unique affiliate names from ad_performance table
      const { data: adAffiliates, error: adError } = await supabase
        .from("ad_performance")
        .select("affiliate_name")
        .not("affiliate_name", "is", null);

      if (adError) throw adError;

      // Get usernames from profiles table
      const { data: profileUsers, error: profileError } = await supabase
        .from("profiles")
        .select("username")
        .not("username", "is", null);

      if (profileError) throw profileError;

      // Combine and deduplicate
      const allNames = new Set();

      adAffiliates?.forEach((item) => {
        if (item.affiliate_name && item.affiliate_name.trim()) {
          allNames.add(item.affiliate_name.trim());
        }
      });

      profileUsers?.forEach((item) => {
        if (item.username && item.username.trim()) {
          allNames.add(item.username.trim());
        }
      });

      // Convert to array and sort
      return Array.from(allNames).sort();
    } catch (err) {
      console.error("Error fetching existing affiliate names:", err);
      return [];
    }
  }, []);

  // Reassign existing ads based on new patterns using database function
  const reassignExistingAds = useCallback(async (affiliateName, patterns) => {
    try {
      const { data, error } = await supabase.rpc("reassign_existing_ads", {
        p_affiliate_name: affiliateName,
        p_patterns: patterns || [],
      });

      if (error) throw error;

      // The function now returns an array with one row containing: reassigned_count, success, message
      const result = data?.[0];

      if (!result) {
        throw new Error("No result returned from reassign function");
      }

      // Check if the operation was successful
      if (!result.success) {
        throw new Error(result.message || "Unknown error during reassignment");
      }

      console.log(`✅ ${result.message}`);
      return result.reassigned_count || 0;
    } catch (err) {
      console.error("Error reassigning existing ads:", err);
      throw err;
    }
  }, []);

  // Get all whitelist entries
  const getWhitelist = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from("affiliate_whitelist")
        .select("*")
        .order("affiliate_name");

      if (queryError) throw queryError;

      return data || [];
    } catch (err) {
      console.error("Error fetching whitelist:", err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get active whitelist (for extractAffiliateName function)
  const getActiveWhitelist = useCallback(async () => {
    try {
      const { data, error: queryError } = await supabase.rpc(
        "get_active_whitelist"
      );

      if (queryError) throw queryError;

      return data || [];
    } catch (err) {
      console.error("Error fetching active whitelist:", err);
      return [];
    }
  }, []);

  // Add new affiliate to whitelist
  const addToWhitelist = useCallback(
    async (affiliateName, patterns = [], autoReassign = true) => {
      try {
        setIsLoading(true);
        setError(null);

        // Validate input
        if (!affiliateName || !affiliateName.trim()) {
          throw new Error("Affiliate name is required");
        }

        // Check if affiliate already exists in whitelist
        const { data: existingEntries, error: checkError } = await supabase
          .from("affiliate_whitelist")
          .select("id, affiliate_name")
          .eq("affiliate_name", affiliateName.trim());

        if (checkError) {
          throw new Error(
            `Error checking for existing affiliate: ${checkError.message}`
          );
        }

        if (existingEntries && existingEntries.length > 0) {
          throw new Error(
            `Affiliate "${affiliateName.trim()}" already exists in whitelist. Use the edit function to update it.`
          );
        }

        // Clean up patterns array
        const cleanPatterns = patterns
          .filter((p) => p && p.trim())
          .map((p) => p.trim().toLowerCase());

        const { data, error: insertError } = await supabase
          .from("affiliate_whitelist")
          .insert({
            affiliate_name: affiliateName.trim(),
            patterns: cleanPatterns,
            is_active: true,
            created_by: user?.id,
          })
          .select()
          .single();

        if (insertError) {
          // Handle duplicate key error with a user-friendly message
          if (insertError.code === "23505") {
            throw new Error(
              `Affiliate "${affiliateName.trim()}" already exists in whitelist. Use the edit function to update it.`
            );
          }
          throw insertError;
        }

        // Automatically reassign existing ads if requested
        let reassignedCount = 0;
        if (autoReassign) {
          reassignedCount = await reassignExistingAds(
            affiliateName.trim(),
            cleanPatterns
          );
        }

        const successMessage =
          reassignedCount > 0
            ? `Successfully added "${affiliateName}" to whitelist and reassigned ${reassignedCount} existing ads`
            : `Successfully added "${affiliateName}" to whitelist`;

        setSuccess(successMessage);
        clearWhitelistCache(); // Clear cache so new data is fetched

        // Rebuild pattern cache after adding affiliate
        try {
          await supabase.rpc("rebuild_affiliate_pattern_cache");
          console.log("✅ Pattern cache rebuilt after adding affiliate");
        } catch (cacheError) {
          console.warn(
            "⚠️ Failed to rebuild pattern cache:",
            cacheError.message
          );
        }

        return { data, reassignedCount };
      } catch (err) {
        console.error("Error adding to whitelist:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id, reassignExistingAds]
  );

  // Update existing whitelist entry
  const updateWhitelist = useCallback(
    async (id, updates, autoReassign = true) => {
      try {
        setIsLoading(true);
        setError(null);

        // Clean up patterns if provided
        if (updates.patterns) {
          updates.patterns = updates.patterns
            .filter((p) => p && p.trim())
            .map((p) => p.trim().toLowerCase());
        }

        const { data, error: updateError } = await supabase
          .from("affiliate_whitelist")
          .update(updates)
          .eq("id", id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Automatically reassign existing ads if patterns were updated
        let reassignedCount = 0;
        if (autoReassign && updates.patterns && updates.affiliate_name) {
          reassignedCount = await reassignExistingAds(
            updates.affiliate_name,
            updates.patterns
          );
        }

        const successMessage =
          reassignedCount > 0
            ? `Successfully updated whitelist entry and reassigned ${reassignedCount} existing ads`
            : `Successfully updated whitelist entry`;

        setSuccess(successMessage);
        clearWhitelistCache(); // Clear cache so new data is fetched

        // Rebuild pattern cache after updating affiliate
        try {
          await supabase.rpc("rebuild_affiliate_pattern_cache");
          console.log("✅ Pattern cache rebuilt after updating affiliate");
        } catch (cacheError) {
          console.warn(
            "⚠️ Failed to rebuild pattern cache:",
            cacheError.message
          );
        }

        return { data, reassignedCount };
      } catch (err) {
        console.error("Error updating whitelist:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [reassignExistingAds]
  );

  // Delete whitelist entry
  const deleteFromWhitelist = useCallback(async (id) => {
    try {
      setIsLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from("affiliate_whitelist")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      setSuccess("Successfully removed from whitelist");
      clearWhitelistCache(); // Clear cache so new data is fetched

      // Rebuild pattern cache after deleting affiliate
      try {
        await supabase.rpc("rebuild_affiliate_pattern_cache");
        console.log("✅ Pattern cache rebuilt after deleting affiliate");
      } catch (cacheError) {
        console.warn("⚠️ Failed to rebuild pattern cache:", cacheError.message);
      }

      return true;
    } catch (err) {
      console.error("Error deleting from whitelist:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toggle active status
  const toggleActive = useCallback(async (id, isActive) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: updateError } = await supabase
        .from("affiliate_whitelist")
        .update({ is_active: isActive })
        .eq("id", id)
        .select()
        .single();

      if (updateError) throw updateError;

      setSuccess(
        `Successfully ${isActive ? "activated" : "deactivated"} whitelist entry`
      );
      clearWhitelistCache(); // Clear cache so new data is fetched

      // Rebuild pattern cache after toggling affiliate status
      try {
        await supabase.rpc("rebuild_affiliate_pattern_cache");
        console.log("✅ Pattern cache rebuilt after toggling affiliate status");
      } catch (cacheError) {
        console.warn("⚠️ Failed to rebuild pattern cache:", cacheError.message);
      }

      return data;
    } catch (err) {
      console.error("Error toggling whitelist status:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Bulk import whitelist entries
  const bulkImportWhitelist = useCallback(
    async (entries) => {
      try {
        setIsLoading(true);
        setError(null);

        const formattedEntries = entries.map((entry) => ({
          affiliate_name: entry.name.trim(),
          patterns: entry.patterns || [],
          is_active: entry.is_active !== false,
          created_by: user?.id,
        }));

        const { data, error: insertError } = await supabase
          .from("affiliate_whitelist")
          .upsert(formattedEntries, { onConflict: "affiliate_name" })
          .select();

        if (insertError) throw insertError;

        setSuccess(`Successfully imported ${data.length} whitelist entries`);
        clearWhitelistCache(); // Clear cache so new data is fetched

        // Rebuild pattern cache after bulk import
        try {
          await supabase.rpc("rebuild_affiliate_pattern_cache");
          console.log("✅ Pattern cache rebuilt after bulk import");
        } catch (cacheError) {
          console.warn(
            "⚠️ Failed to rebuild pattern cache:",
            cacheError.message
          );
        }

        return data;
      } catch (err) {
        console.error("Error bulk importing whitelist:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id]
  );

  // Manual reassign function for existing data
  const manualReassignAds = useCallback(
    async (affiliateName, patterns) => {
      try {
        setIsLoading(true);
        setError(null);

        const reassignedCount = await reassignExistingAds(
          affiliateName,
          patterns
        );

        setSuccess(
          `Successfully reassigned ${reassignedCount} existing ads to "${affiliateName}"`
        );

        // Rebuild pattern cache after manual reassignment
        try {
          await supabase.rpc("rebuild_affiliate_pattern_cache");
          console.log("✅ Pattern cache rebuilt after manual reassignment");
        } catch (cacheError) {
          console.warn(
            "⚠️ Failed to rebuild pattern cache:",
            cacheError.message
          );
        }

        return reassignedCount;
      } catch (err) {
        console.error("Error manually reassigning ads:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [reassignExistingAds]
  );

  // Function to rebuild earnings summary for a specific affiliate
  const rebuildEarningSummary = async (affiliateName) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log(`🔄 Rebuilding earnings summary for ${affiliateName}...`);

      // First, delete existing summary records for this affiliate
      const { error: deleteError } = await supabase
        .from("affiliate_earnings_summary")
        .delete()
        .eq("affiliate_name", affiliateName);

      if (deleteError) {
        throw new Error(
          `Failed to clear existing summary: ${deleteError.message}`
        );
      }

      console.log(`✅ Cleared existing summary data for ${affiliateName}`);

      // SIMPLIFIED APPROACH: Direct SQL aggregation instead of CSV processing
      // This bypasses the problematic normalizeAdName() aggregation that was causing data loss

      // Get aggregated data directly from ad_performance using SQL
      console.log(
        `📊 Attempting to use SQL function get_affiliate_summary_direct for ${affiliateName}...`
      );

      const { data: aggregatedData, error: aggregateError } =
        await supabase.rpc("get_affiliate_summary_direct", {
          p_affiliate_name: affiliateName,
        });

      if (aggregateError) {
        // Fallback: If RPC doesn't exist, do manual aggregation
        console.log("⚠️ RPC not available, using fallback aggregation...");
        console.log("RPC Error:", aggregateError);

        // Get all raw data for this affiliate with better error handling
        console.log(
          `📥 Fetching raw ad_performance data for ${affiliateName}...`
        );

        // First, get the whitelist patterns for this affiliate
        const { data: whitelistData, error: whitelistError } = await supabase
          .from("affiliate_whitelist")
          .select("patterns")
          .eq("affiliate_name", affiliateName)
          .eq("is_active", true)
          .single();

        if (whitelistError) {
          console.error(
            `❌ Error fetching whitelist for ${affiliateName}:`,
            whitelistError
          );
          throw new Error(
            `Failed to fetch whitelist patterns: ${whitelistError.message}`
          );
        }

        const patterns = whitelistData?.patterns || [];
        console.log(`📋 Patterns for ${affiliateName}:`, patterns);

        // Check if affiliate name matches any of the patterns (case-insensitive)
        const affiliateNameLower = affiliateName.toLowerCase();
        const nameMatchesPattern = patterns.some(
          (pattern) => pattern.toLowerCase() === affiliateNameLower
        );

        console.log(`🔍 Name matches pattern check: ${nameMatchesPattern}`);

        let allAdData = [];
        let from = 0;
        const pageSize = 1000;
        let totalFetched = 0;

        try {
          while (true) {
            console.log(`📥 Fetching batch starting at ${from}...`);

            let pageData = [];

            // If affiliate name matches a pattern OR has no patterns, use exact affiliate_name matching
            if (nameMatchesPattern || patterns.length === 0) {
              console.log(
                `🔍 Using exact affiliate_name match for ${affiliateName}`
              );

              const { data: rawData, error: pageError } = await supabase
                .from("ad_performance")
                .select("*")
                .eq("affiliate_name", affiliateName)
                .range(from, from + pageSize - 1);

              if (pageError) {
                console.error(`❌ Error fetching batch at ${from}:`, pageError);
                throw new Error(
                  `Failed to fetch ad data: ${pageError.message}`
                );
              }

              pageData = rawData || [];
            } else if (patterns && patterns.length > 0) {
              console.log(`🔍 Using pattern matching for ${affiliateName}`);

              // For pattern matching, we need to get ALL data and filter it
              // This is a one-time operation, so we'll get all data in the first iteration
              if (from === 0) {
                console.log(
                  `📥 Fetching ALL ad_performance data for pattern matching...`
                );

                const { data: rawData, error: pageError } = await supabase
                  .from("ad_performance")
                  .select("*");

                if (pageError) {
                  console.error(`❌ Error fetching all data:`, pageError);
                  throw new Error(
                    `Failed to fetch ad data: ${pageError.message}`
                  );
                }

                // Filter by patterns
                pageData =
                  rawData?.filter((row) => {
                    const adNameLower = (row.ad_name || "").toLowerCase();
                    return patterns.some((pattern) =>
                      adNameLower.includes(pattern.toLowerCase())
                    );
                  }) || [];

                console.log(
                  `📊 Found ${pageData.length} records matching patterns for ${affiliateName}`
                );
              } else {
                // We already got all data in the first iteration
                pageData = [];
              }
            } else {
              // Use exact affiliate_name match with pagination
              console.log(
                `🔍 Using exact affiliate_name match for ${affiliateName}`
              );

              const { data: rawData, error: pageError } = await supabase
                .from("ad_performance")
                .select("*")
                .eq("affiliate_name", affiliateName)
                .range(from, from + pageSize - 1);

              if (pageError) {
                console.error(`❌ Error fetching batch at ${from}:`, pageError);
                throw new Error(
                  `Failed to fetch ad data: ${pageError.message}`
                );
              }

              pageData = rawData || [];
            }

            if (!pageData || pageData.length === 0) {
              console.log(
                `✅ No more data found, stopping at ${totalFetched} records`
              );
              break;
            }

            allAdData = [...allAdData, ...pageData];
            totalFetched += pageData.length;

            console.log(
              `✅ Fetched batch: ${pageData.length} records (total: ${totalFetched})`
            );

            // Handle pagination based on the method used
            if (!nameMatchesPattern && patterns && patterns.length > 0) {
              // For pattern matching in ad_name, we got all data in one go, so break after first iteration
              console.log(
                `✅ Pattern matching completed, total records: ${totalFetched}`
              );
              break;
            } else {
              // For exact affiliate_name match, continue until we get less than pageSize
              if (pageData.length < pageSize) {
                console.log(
                  `✅ Exact match completed, total records: ${totalFetched}`
                );
                break;
              }
            }

            from += pageSize;

            // Add a small delay to prevent overwhelming the database
            if (totalFetched > 10000) {
              console.log(`⏳ Large dataset detected, adding small delay...`);
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
        } catch (fetchError) {
          console.error(
            `❌ Critical error during data fetch for ${affiliateName}:`,
            fetchError
          );
          throw fetchError;
        }

        if (!allAdData || allAdData.length === 0) {
          console.log(
            `⚠️ No ad data found for ${affiliateName} in ad_performance table`
          );
          setSuccess(
            `No ad data found for ${affiliateName} to rebuild summary`
          );
          return 0;
        }

        console.log(
          `📊 Found ${allAdData.length} ad records for ${affiliateName} in ad_performance table`
        );

        // Log sample data to debug
        console.log("📋 Sample raw data (first 3 records):");
        allAdData.slice(0, 3).forEach((row, i) => {
          console.log(`  Record ${i + 1}:`, {
            ad_name: row.ad_name,
            spend: row.spend,
            revenue: row.revenue,
            roas: row.spend > 0 ? (row.revenue / row.spend).toFixed(2) : 0,
            existing_commission: row.commission,
          });
        });

        // For large datasets, show progress during processing
        const isLargeDataset = allAdData.length > 10000;
        if (isLargeDataset) {
          console.log(
            `⚠️ Large dataset detected (${allAdData.length} records). Processing with progress updates...`
          );
        }

        // Create summary records directly from raw data with deduplication
        const recordMap = new Map();
        let totalSpend = 0;
        let totalRevenue = 0;
        let duplicateCount = 0;

        allAdData.forEach((row, index) => {
          // Show progress for large datasets
          if (isLargeDataset && index > 0 && index % 5000 === 0) {
            console.log(
              `📊 Processing progress: ${index}/${allAdData.length} records (${((index / allAdData.length) * 100).toFixed(1)}%)`
            );
          }
          const safeNumber = (value, defaultValue = 0) => {
            if (value === "" || value === null || value === undefined)
              return defaultValue;
            const num = parseFloat(value);
            return isNaN(num) ? defaultValue : num;
          };

          const safeInteger = (value, defaultValue = 0) => {
            if (value === "" || value === null || value === undefined)
              return defaultValue;
            const num = parseInt(value);
            return isNaN(num) ? defaultValue : num;
          };

          // Create unique key for deduplication
          const uniqueKey = `${row.affiliate_name}|||${row.ad_name}`;

          const spend = safeNumber(row.spend, 0);
          const revenue = safeNumber(row.revenue, 0);
          const roas = spend > 0 ? revenue / spend : 0;

          // Track totals for logging
          totalSpend += spend;
          totalRevenue += revenue;

          if (!recordMap.has(uniqueKey)) {
            recordMap.set(uniqueKey, {
              affiliate_name: row.affiliate_name,
              ad_name: row.ad_name,
              ad_spend: spend,
              revenue: revenue,
              roas: roas,
              profit: safeNumber(row.profit, 0),
              commission: 0, // Will be calculated after aggregation
              purchases: safeInteger(row.purchases, 0),
              cpa: safeNumber(row.cpa, null),
              aov: safeNumber(row.aov, null),
              campaign_name: row.campaign_name || "",
              adset_name: row.adset_name || "",
              commission_tier: "",
              commission_label: "",
              brand: row.brand || "",
            });

            // Log commission calculation for debugging (first 5 records only)
            if (index < 5) {
              const roas = spend > 0 ? revenue / spend : 0;
              const tieredCommission = calculateTieredCommission(roas, spend);
              console.log(`💰 Commission calculation for ${row.ad_name}:`, {
                spend: spend,
                revenue: revenue,
                roas: roas.toFixed(2),
                tiered_commission: tieredCommission.commission.toFixed(2),
                tier_rate: tieredCommission.label,
              });
            }
          } else {
            // If duplicate found, aggregate the values
            duplicateCount++;
            const existing = recordMap.get(uniqueKey);
            existing.ad_spend += spend;
            existing.revenue += revenue;
            existing.profit += safeNumber(row.profit, 0);
            existing.purchases += safeInteger(row.purchases, 0);
            // Recalculate ROAS after aggregation
            existing.roas =
              existing.ad_spend > 0 ? existing.revenue / existing.ad_spend : 0;
          }
        });

        // NOW calculate commission for each aggregated record (this is the correct approach)
        const summaryRecords = Array.from(recordMap.values());
        let totalCommissionCalculated = 0;

        summaryRecords.forEach((record) => {
          const roas =
            record.ad_spend > 0 ? record.revenue / record.ad_spend : 0;
          const result = calculateTieredCommission(roas, record.ad_spend);
          record.commission = result.commission;
          totalCommissionCalculated += record.commission;
        });

        console.log(`📈 Processing Summary for ${affiliateName}:`);
        console.log(`  • Raw records processed: ${allAdData.length}`);
        console.log(
          `  • Unique ads after deduplication: ${summaryRecords.length}`
        );
        console.log(`  • Duplicate records found: ${duplicateCount}`);
        console.log(`  • Total spend: $${totalSpend.toFixed(2)}`);
        console.log(`  • Total revenue: $${totalRevenue.toFixed(2)}`);
        console.log(
          `  • Overall ROAS: ${totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : 0}`
        );
        console.log(
          `  • Total tiered commission: $${totalCommissionCalculated.toFixed(2)}`
        );

        // Calculate final totals from summary records (should match totalCommissionCalculated)
        const finalTotals = summaryRecords.reduce(
          (acc, record) => ({
            spend: acc.spend + record.ad_spend,
            revenue: acc.revenue + record.revenue,
            commission: acc.commission + record.commission,
          }),
          { spend: 0, revenue: 0, commission: 0 }
        );

        // Verify that our commission calculations match
        console.log(`🔍 Commission Verification for ${affiliateName}:`);
        console.log(
          `  • Commission from individual calculation: $${totalCommissionCalculated.toFixed(2)}`
        );
        console.log(
          `  • Commission from final totals: $${finalTotals.commission.toFixed(2)}`
        );
        console.log(
          `  • Match: ${Math.abs(totalCommissionCalculated - finalTotals.commission) < 0.01 ? "✅ YES" : "❌ NO"}`
        );

        // Use the correct commission value
        const correctCommission = totalCommissionCalculated;

        console.log(`📊 Final Summary Totals for ${affiliateName}:`);
        console.log(`  • Final spend: $${finalTotals.spend.toFixed(2)}`);
        console.log(`  • Final revenue: $${finalTotals.revenue.toFixed(2)}`);
        console.log(
          `  • Final ROAS: ${finalTotals.spend > 0 ? (finalTotals.revenue / finalTotals.spend).toFixed(2) : 0}`
        );
        console.log(`  • Final commission: $${correctCommission.toFixed(2)}`);

        // Insert summary records in batches
        const batchSize = 100;
        let insertedCount = 0;

        console.log(
          `💾 Inserting ${summaryRecords.length} summary records in batches of ${batchSize}...`
        );

        // Log sample records being inserted
        if (summaryRecords.length > 0) {
          console.log(`📋 Sample records being inserted:`);
          summaryRecords.slice(0, 3).forEach((record, index) => {
            console.log(`  Record ${index + 1}:`, {
              affiliate_name: record.affiliate_name,
              ad_name: record.ad_name,
              ad_spend: record.ad_spend,
              revenue: record.revenue,
              commission: record.commission,
            });
          });
        }

        for (let i = 0; i < summaryRecords.length; i += batchSize) {
          const batch = summaryRecords.slice(i, i + batchSize);

          console.log(
            `📤 Upserting batch ${Math.ceil((i + 1) / batchSize)} with ${batch.length} records...`
          );

          const { data: upsertData, error: upsertError } = await supabase
            .from("affiliate_earnings_summary")
            .upsert(batch, {
              onConflict: "affiliate_name,ad_name",
              ignoreDuplicates: false,
            })
            .select();

          if (upsertError) {
            console.error("❌ Upsert error:", upsertError);
            console.error("Problematic batch:", batch);
            console.error("Sample problematic record:", batch[0]);
            throw upsertError;
          }

          console.log(
            `✅ Upsert successful, returned ${upsertData?.length || 0} records`
          );
          if (upsertData && upsertData.length > 0) {
            console.log(`📋 Sample upserted record:`, upsertData[0]);
          }

          insertedCount += batch.length;
          console.log(
            `✅ Processed batch ${Math.ceil((i + 1) / batchSize)} of ${Math.ceil(summaryRecords.length / batchSize)}`
          );
        }

        console.log(
          `🎉 Successfully rebuilt earnings summary for ${affiliateName}:`
        );
        console.log(`  • Records inserted: ${insertedCount}`);
        console.log(
          `  • Expected commission: $${correctCommission.toFixed(2)}`
        );

        // Verify what was actually inserted - check ALL records
        const { data: verifyData, error: verifyError } = await supabase
          .from("affiliate_earnings_summary")
          .select("affiliate_name, ad_spend, revenue, commission")
          .eq("affiliate_name", affiliateName);

        if (verifyError) {
          console.error("❌ Error verifying inserted data:", verifyError);
        } else {
          console.log(
            `🔍 Verification - Data actually in summary table for ${affiliateName}:`
          );
          const totalVerifySpend = verifyData.reduce(
            (sum, row) => sum + (row.ad_spend || 0),
            0
          );
          const totalVerifyRevenue = verifyData.reduce(
            (sum, row) => sum + (row.revenue || 0),
            0
          );
          const totalVerifyCommission = verifyData.reduce(
            (sum, row) => sum + (row.commission || 0),
            0
          );

          console.log(`  • Records in summary table: ${verifyData.length}`);
          console.log(
            `  • Total spend in summary: $${totalVerifySpend.toFixed(2)}`
          );
          console.log(
            `  • Total revenue in summary: $${totalVerifyRevenue.toFixed(2)}`
          );
          console.log(
            `  • Total commission in summary: $${totalVerifyCommission.toFixed(2)}`
          );

          if (verifyData.length > 0) {
            console.log(`  • Sample record:`, verifyData[0]);
          }
        }

        setSuccess(
          `✅ Successfully rebuilt earnings summary for ${affiliateName}: ${insertedCount} records processed (1:1 mapping, no aggregation). Expected commission: $${correctCommission.toFixed(2)}`
        );

        // Force refresh dashboard data by clearing any cache
        console.log(`🔄 Forcing dashboard data refresh...`);

        // Trigger a custom event to notify dashboard to refresh
        window.dispatchEvent(
          new CustomEvent("affiliateDataUpdated", {
            detail: { affiliateName, action: "rebuild" },
          })
        );

        return insertedCount;
      }

      // If RPC worked, process the aggregated results
      if (aggregatedData && aggregatedData.length > 0) {
        const insertedCount = aggregatedData[0].records_inserted || 0;
        console.log(`✅ SQL function succeeded for ${affiliateName}:`);
        console.log(`  • Records inserted via SQL: ${insertedCount}`);

        setSuccess(
          `✅ Successfully rebuilt earnings summary for ${affiliateName}: ${insertedCount} records processed via SQL`
        );
        return insertedCount;
      }

      console.log(`⚠️ No data found for ${affiliateName}`);
      setSuccess(`No data found for ${affiliateName}`);
      return 0;
    } catch (err) {
      console.error("❌ Error rebuilding earnings summary:", err);
      setError(`Failed to rebuild earnings summary: ${err.message}`);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const rebuildAllEarningSummaries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      console.log(
        "🚀 Calling the DEFINITIVE server-side function to rebuild all summaries..."
      );

      // Step 1: Call the single, correct SQL function to perform the entire rebuild.
      const { data: result, error: sqlError } = await supabase.rpc(
        "rebuild_all_summaries_definitive"
      );

      if (sqlError) {
        console.error("❌ Definitive rebuild function error:", sqlError);
        throw new Error(
          `The definitive server-side rebuild failed: ${sqlError.message}`
        );
      }

      console.log("✅ Server-side function executed successfully.");
      console.log("📊 Overall Results:", result);

      // Step 2: Fetch detailed results from the newly populated summary table for logging.
      console.log("📝 Fetching detailed results for logging...");
      const { data: logData, error: logError } = await supabase
        .from("affiliate_earnings_summary")
        .select("affiliate_name, ad_spend, revenue, commission")
        .order("affiliate_name");

      if (logError) {
        console.error("Could not fetch details for logging:", logError);
      } else {
        // Process the log data to group by affiliate
        const affiliateLogs = logData.reduce((acc, row) => {
          if (!acc[row.affiliate_name]) {
            acc[row.affiliate_name] = {
              adCount: 0,
              totalSpend: 0,
              totalRevenue: 0,
              totalCommission: 0,
            };
          }
          acc[row.affiliate_name].adCount++;
          acc[row.affiliate_name].totalSpend += row.ad_spend || 0;
          acc[row.affiliate_name].totalRevenue += row.revenue || 0;
          acc[row.affiliate_name].totalCommission += row.commission || 0;
          return acc;
        }, {});

        console.log("📋 Detailed Rebuild Log:");
        for (const [name, data] of Object.entries(affiliateLogs)) {
          console.log(
            `  -> Processed ${name}: ${
              data.adCount
            } ads, Spend: $${data.totalSpend.toFixed(
              2
            )}, Commission: $${data.totalCommission.toFixed(2)}`
          );
        }
      }

      const successMessage = `${
        result.message
      } Processed ${result.total_records_inserted?.toLocaleString()} records for ${
        result.total_affiliates_processed
      } affiliates. Total commission: $${result.total_commission_calculated?.toLocaleString(
        undefined,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}.`;

      setSuccess(successMessage);
      return result;
    } catch (err) {
      console.error("❌ Error rebuilding all earnings summaries:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    success,
    clearMessages,
    getExistingAffiliateNames,
    getWhitelist,
    getActiveWhitelist,
    addToWhitelist,
    updateWhitelist,
    deleteFromWhitelist,
    toggleActive,
    bulkImportWhitelist,
    manualReassignAds,
    reassignExistingAds,
    rebuildEarningSummary,
    rebuildAllEarningSummaries,
  };
};

export default useWhitelistManager;
